export default class Input {
  #element: HTMLInputElement;
  #oldValue: string = '';
  #touched: boolean = false;

  /**
   * Instantiate a new input class
   */
  constructor(element: HTMLElement) {
    if (!(element instanceof HTMLInputElement)) {
      throw new Error('The given element must by an input element');
    }

    this.#element = element;

    if (this.hasValue()) {
      this.rememberValue();
    }
  }

  /**
   * Get the element
   */
  get element(): HTMLInputElement {
    return this.#element;
  }

  /**
   * Get the old value of the input
   */
  get oldValue(): string {
    return this.#oldValue;
  }

  /**
   * Get the value of the input field element
   */
  get value(): string {
    return this.#element.value;
  }

  /**
   * Set the value of the input field element
   */
  set value(value: string): void {
    this.rememberValue();
    this.#element.value = value;
  }

  /**
   * Get a given input instance by its element
   */
  // eslint-disable-next-line no-use-before-define
  static getInputByElement(inputs: Array<Input>, element: HTMLElement): ?Input {
    return inputs.find((input: Input) => input.element === element);
  }

  /**
   * Check whether the input has a value
   */
  hasValue(): boolean {
    return !!this.value;
  }

  /**
   * Remember the value of the input
   */
  rememberValue() {
    this.#oldValue = this.value;
    this.#touched = true;
  }

  /**
   * Check if the value of the input has changed
   */
  valueHasChanged(): boolean {
    return this.#oldValue !== this.value;
  }

  /**
   * Check whether the input is valid
   */
  isValid(): boolean {
    if (this.#element.hasAttribute('disabled')) {
      return true;
    }

    const clone: HTMLInputElement = this.#element.cloneNode();
    clone.value = clone.value.trim();
    return clone.validity.valid;
  }

  /**
   * Check whether the input has reached its maximum length
   */
  hasReachedMaximumLength(): boolean {
    return this.value.length >= this.#element.maxLength;
  }

  /**
   * Focus the element
   */
  focus(): void {
    this.#element.focus();
  }

  /**
   * Check if the input has been touched
   */
  hasBeenTouched(): boolean {
    return this.#touched;
  }
}
